import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import ListLayout from "../../../components/Layout/ListLayout";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Snackbar, Alert, TablePagination, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import DataListFilters from "../../../components/Admin/DataLists/DataListFilters";
import DataListTable from "../../../components/Admin/DataLists/DataListTable";
import DataListForm from "../../../components/Admin/DataLists/DataListForm";
import DataListItemTable from "../../../components/Admin/DataLists/DataListItemTable";
import DataListItemForm from "../../../components/Admin/DataLists/DataListItemForm";
import DataListImportDialog from "../../../components/Admin/DataLists/DataListImportDialog";
import DataListCreateFromCsvDialog from "../../../components/Admin/DataLists/DataListCreateFromCsvDialog";
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../auth";
import {
  getListDataListQueryKey,
  getListDataListQueryOptions,
  getListDataListRowQueryKey,
  getListDataListRowQueryOptions,
  getCreateDataListMutationOptions,
  getEditDataListMutationOptions,
  getDeleteDataListMutationOptions,
  getActivateDataListMutationOptions,
  getDisableDataListMutationOptions,
  getCreateDataListRowMutationOptions,
  getEditDataListRowMutationOptions,
  getDeleteDataListRowMutationOptions,
  getActivateDataListRowMutationOptions,
  getDisableDataListRowMutationOptions
} from "../../../api/bpm/endpoints";
import type { DataListDto } from "../../../api/bpm/model";
import { exportDataListCsv } from "../../../api/bpm/dataListsCsv";

const dataListsSearchSchema = z.object({
  page: z.number().catch(1),
  size: z.number().catch(10),
  q: z.string().optional(),
});

export const Route = createFileRoute('/__auth/admin/data-lists')({
  component: DataListsComponent,
  validateSearch: (search) => dataListsSearchSchema.parse(search),
  staticData: {
    title: "dataLists.title",
  },
});

function DataListsComponent() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const { currentTenant } = useAuth();

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const [isDataListDialogOpen, setIsDataListDialogOpen] = useState(false);
  const [selectedDataList, setSelectedDataList] = useState<DataListDto | null>(null);
  const [dataListToDelete, setDataListToDelete] = useState<DataListDto | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const [isItemsDialogOpen, setIsItemsDialogOpen] = useState(false);
  const [itemsPage, setItemsPage] = useState(0);
  const [itemsRowsPerPage, setItemsRowsPerPage] = useState(10);
  const [selectedItemForEdit, setSelectedItemForEdit] = useState<any | null>(null);
  const [itemToDelete, setItemToDelete] = useState<any | null>(null);
  const [isDeleteItemDialogOpen, setIsDeleteItemDialogOpen] = useState(false);
  const [isItemFormDialogOpen, setIsItemFormDialogOpen] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isCreateFromCsvDialogOpen, setIsCreateFromCsvDialogOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const showSnackbar = (message: string, severity: 'success' | 'error' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const effectiveTenant = currentTenant || "default-domain";
  const dataListsQueryKey = getListDataListQueryKey(effectiveTenant);

  const refreshDataLists = () =>
    queryClient.invalidateQueries({
      queryKey: dataListsQueryKey,
    });

  const refreshDataListItems = () => {
    if (!selectedDataList?.id) {
      return;
    }

    queryClient.invalidateQueries({
      queryKey: getListDataListRowQueryKey(effectiveTenant, selectedDataList.id),
    });
  };

  const getDataListItemLabel = (item: any) =>
    item?.name || item?.label || item?.description || item?.id || "";

  const { data: listsData, isLoading, error } = useQuery({
    ...getListDataListQueryOptions(effectiveTenant, {
            q: search.q || undefined,
            page: (search.page || 1) - 1,
            size: search.size || 10
        } as any),
    staleTime: 30000,
  });

  const rows: DataListDto[] = useMemo(() => {
    const content = (listsData as any)?.data || listsData;
    return (content as any)?.content ?? (Array.isArray(content) ? content : []);
  }, [listsData]);

  const totalElements = useMemo(() => {
    const content = (listsData as any)?.data || listsData;
    return (content as any)?.totalElements ?? rows.length;
  }, [listsData, rows.length]);

  const itemsQueryOptions = selectedDataList?.id
    ? getListDataListRowQueryOptions(effectiveTenant, selectedDataList.id, {
            page: itemsPage,
            size: itemsRowsPerPage,
            sort: ["id,asc"],
            isPageable: true
        } as any)
    : {
        queryKey: ["data-list-items", effectiveTenant, "none", itemsPage, itemsRowsPerPage],
        queryFn: () => ({ data: { content: [], totalElements: 0 } } as any),
      };

  const { data: itemsData, isLoading: isLoadingItems } = useQuery({
    ...(itemsQueryOptions as any),
    enabled: !!selectedDataList?.id && isItemsDialogOpen,
    select: (res: any) => {
      const content = res?.data || res;
      return {
        content: content?.content ?? (Array.isArray(content) ? content : []),
        totalElements: content?.totalElements ?? (Array.isArray(content) ? content.length : 0),
      };
    }
  });

  const handlePageChange = (_: unknown, value: number) => {
    navigate({
      search: (prev) => ({ ...prev, page: value }),
      replace: true
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleFilter = (newFilters: { q?: string }) => {
    navigate({
      search: (prev) => ({
        ...prev,
        ...newFilters,
        page: 1
      }),
    });
  };

  const createDataListMutation = useMutation({
    ...getCreateDataListMutationOptions(),
    onSuccess: () => {
      refreshDataLists();
      showSnackbar(`Lista criada com sucesso`);
      setIsDataListDialogOpen(false);
    },
    onError: () => showSnackbar("Erro ao salvar lista", "error"),
  });

  const editDataListMutation = useMutation({
    ...getEditDataListMutationOptions(),
    onSuccess: () => {
      refreshDataLists();
      showSnackbar(`Lista atualizada com sucesso`);
      setIsDataListDialogOpen(false);
    },
    onError: () => showSnackbar("Erro ao salvar lista", "error"),
  });

  const deleteDataListMutation = useMutation({
    ...getDeleteDataListMutationOptions(),
    onSuccess: () => {
      refreshDataLists();
      showSnackbar("Lista excluída com sucesso");
    },
    onError: () => showSnackbar("Erro ao excluir lista", "error"),
  });

  const activateDataListMut = useMutation({
    ...getActivateDataListMutationOptions(),
    onSuccess: refreshDataLists,
  });

  const disableDataListMut = useMutation({
    ...getDisableDataListMutationOptions(),
    onSuccess: refreshDataLists,
  });

  const createItemMutation = useMutation({
    ...getCreateDataListRowMutationOptions(),
    onSuccess: () => {
      refreshDataListItems();
      showSnackbar(`Item criado com sucesso`);
      setIsItemFormDialogOpen(false);
    },
    onError: () => showSnackbar("Erro ao salvar item", "error"),
  });

  const editItemMutation = useMutation({
    ...getEditDataListRowMutationOptions(),
    onSuccess: () => {
      refreshDataListItems();
      showSnackbar(`Item atualizado com sucesso`);
      setIsItemFormDialogOpen(false);
    },
    onError: () => showSnackbar("Erro ao salvar item", "error"),
  });

  const deleteItemMutation = useMutation({
    ...getDeleteDataListRowMutationOptions(),
    onSuccess: () => {
      refreshDataListItems();
      showSnackbar("Item excluído com sucesso");
    },
  });

  const activateItemMut = useMutation({
    ...getActivateDataListRowMutationOptions(),
    onSuccess: refreshDataListItems,
  });

  const disableItemMut = useMutation({
    ...getDisableDataListRowMutationOptions(),
    onSuccess: refreshDataListItems,
  });

  const handleExport = async (dataList: DataListDto) => {
    if (!dataList.id) return;
    setIsExporting(true);
    try {
      const blob = await exportDataListCsv(effectiveTenant, dataList.id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const safeName = (dataList.name || "lista-de-dados")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-zA-Z0-9_-]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .toLocaleLowerCase();
      link.href = url;
      link.download = `${safeName || "lista-de-dados"}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      showSnackbar(t("dataLists.csv.exportSuccess"));
    } catch (requestError: any) {
      showSnackbar(requestError?.response?.data?.message || t("dataLists.csv.exportError"), "error");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <ListLayout
      title={t("dataLists.title")}
      subtitle={t("dataLists.subtitle")}
      filters={
        <DataListFilters
          onApply={handleFilter}
          isLoading={isLoading}
          initialValues={{ q: search.q }}
        />
      }
      isLoading={isLoading}
      isEmpty={!rows.length}
      error={error as any}
      actions={
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button variant="outlined" startIcon={<FileUploadIcon />} onClick={() => setIsCreateFromCsvDialogOpen(true)}>
            {t("dataLists.csv.create")}
          </Button>
          <IconButton color="primary" onClick={() => { setSelectedDataList(null); setIsDataListDialogOpen(true); }}>
            <AddIcon />
          </IconButton>
        </Box>
      }
    >
      <DataListTable
        data={rows}
        isLoading={isLoading}
        onEdit={(item) => { setSelectedDataList(item); setIsDataListDialogOpen(true); }}
        onDelete={(item) => { setDataListToDelete(item); setIsDeleteDialogOpen(true); }}
        onToggleEnable={(item) => {
          if (item.situation === 'ATIVO') disableDataListMut.mutate({ tenant: effectiveTenant, dataListId: item.id! });
          else activateDataListMut.mutate({ tenant: effectiveTenant, dataListId: item.id! });
        }}
        onManageItems={(item) => { setSelectedDataList(item); setIsItemsDialogOpen(true); }}
        onImport={(item) => { setSelectedDataList(item); setIsImportDialogOpen(true); }}
        onExport={(item) => void handleExport(item)}
        isExporting={isExporting}
      />

      <TablePagination
        component="div"
        count={totalElements}
        page={(search.page || 1) - 1}
        onPageChange={(_, newPage) => handlePageChange(null, newPage + 1)}
        rowsPerPage={search.size || 10}
        onRowsPerPageChange={(e) => {
          const newSize = parseInt(e.target.value, 10);
          navigate({
            search: (prev) => ({ ...prev, size: newSize, page: 1 }),
            replace: true
          });
        }}
        labelRowsPerPage="Linhas por página"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        rowsPerPageOptions={[5, 10, 25, 50]}
        sx={{
          mt: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      />

      <Dialog open={isDataListDialogOpen} onClose={() => setIsDataListDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogContent>
          <DataListForm
            initialValues={selectedDataList || undefined}
            onSubmit={(input) => {
              const { enabled, ...corpo } = input as any;
              
              const mappedColumns = (corpo.columns || []).map((col: any) => ({
                name: col.name,
                label: col.label,
                type: col.type,
                required: col.required,
              }));

              if (selectedDataList?.id) {
                editDataListMutation.mutate({
                  tenant: effectiveTenant,
                  dataListId: selectedDataList.id,
                  data: {
                    description: corpo.description || "",
                    columns: mappedColumns,
                  },
                });
              } else {
                createDataListMutation.mutate({
                  tenant: effectiveTenant,
                  data: {
                    name: corpo.name,
                    description: corpo.description || "",
                    columns: mappedColumns,
                  },
                });
              }
            }}
            onCancel={() => setIsDataListDialogOpen(false)}
            isLoading={createDataListMutation.isPending || editDataListMutation.isPending}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
      >
        <DialogTitle>
          {t("dataLists.deleteTitle")}
        </DialogTitle>
        <DialogContent>
          <Typography>{t("dataLists.deleteConfirm", { name: dataListToDelete?.name })}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setIsDeleteDialogOpen(false)} color="inherit">
            {t("common.cancel")}
          </Button>
          <Button
            onClick={() => {
              if (dataListToDelete?.id) {
                deleteDataListMutation.mutate({ tenant: effectiveTenant, dataListId: dataListToDelete.id });
              }
              setIsDeleteDialogOpen(false);
              setDataListToDelete(null);
            }}
            variant="contained"
            color="error"
          >
            {t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isItemsDialogOpen}
        onClose={() => setIsItemsDialogOpen(false)}
        maxWidth="md"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            maxWidth: "1300px",
            width: "100%",
          },
        }}
      >
        <DialogContent>
          <DataListItemTable
            data={(itemsData as any)?.content || []}
            columns={selectedDataList?.columns || []}
            isLoading={isLoadingItems}
            onAdd={() => { setSelectedItemForEdit(null); setIsItemFormDialogOpen(true); }}
            onEdit={(item) => { setSelectedItemForEdit(item); setIsItemFormDialogOpen(true); }}
            onDelete={(item) => { setItemToDelete(item); setIsDeleteItemDialogOpen(true); }}
            onToggleEnable={(item) => {
              if (item.situation === 'ATIVO') disableItemMut.mutate({ tenant: effectiveTenant, dataListId: selectedDataList!.id!, itemId: item.id! });
              else activateItemMut.mutate({ tenant: effectiveTenant, dataListId: selectedDataList!.id!, itemId: item.id! });
            }}
          />

          <TablePagination
            component="div"
            count={(itemsData as any)?.totalElements || 0}
            page={itemsPage}
            onPageChange={(_, newPage) => setItemsPage(newPage)}
            rowsPerPage={itemsRowsPerPage}
            onRowsPerPageChange={(e) => {
              setItemsRowsPerPage(parseInt(e.target.value, 10));
              setItemsPage(0);
            }}
            labelRowsPerPage="Linhas por página"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={{
              mt: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={isDeleteItemDialogOpen}
        onClose={() => setIsDeleteItemDialogOpen(false)}
      >
        <DialogTitle>
          {t("dataLists.items.deleteTitle")}
        </DialogTitle>
        <DialogContent>
          <Typography>{t("dataLists.items.deleteConfirm", { name: getDataListItemLabel(itemToDelete) })}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setIsDeleteItemDialogOpen(false)} color="inherit">
            {t("common.cancel")}
          </Button>
          <Button
            onClick={() => {
              if (selectedDataList?.id && itemToDelete?.id) {
                deleteItemMutation.mutate({
                  tenant: effectiveTenant,
                  dataListId: selectedDataList.id,
                  itemId: itemToDelete.id,
                });
              }
              setIsDeleteItemDialogOpen(false);
              setItemToDelete(null);
            }}
            variant="contained"
            color="error"
          >
            {t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isItemFormDialogOpen} onClose={() => setIsItemFormDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogContent>
          <DataListItemForm
            initialValues={selectedItemForEdit || undefined}
            columns={selectedDataList?.columns || []}
            onSubmit={(input) => {
              const { enabled, ...corpo } = input as any;
              if (!selectedDataList?.id) return;
              if (selectedItemForEdit?.id) {
                editItemMutation.mutate({
                  tenant: effectiveTenant,
                  dataListId: selectedDataList.id,
                  itemId: selectedItemForEdit.id,
                  data: corpo,
                });
              } else {
                createItemMutation.mutate({
                  tenant: effectiveTenant,
                  dataListId: selectedDataList.id,
                  data: corpo,
                });
              }
            }}
            onCancel={() => setIsItemFormDialogOpen(false)}
            isLoading={createItemMutation.isPending || editItemMutation.isPending}
          />
        </DialogContent>
      </Dialog>

      <DataListImportDialog
        open={isImportDialogOpen}
        tenant={effectiveTenant}
        dataList={selectedDataList}
        onClose={() => setIsImportDialogOpen(false)}
        onSuccess={(result) => {
          setSelectedDataList(result.dataList);
          setItemsPage(0);
          setIsImportDialogOpen(false);
          refreshDataLists();
          queryClient.invalidateQueries({
            queryKey: getListDataListRowQueryKey(effectiveTenant, result.dataList.id!),
          });
          showSnackbar(t("dataLists.csv.importSuccess", { count: result.importedRows }));
        }}
      />

      <DataListCreateFromCsvDialog
        open={isCreateFromCsvDialogOpen}
        tenant={effectiveTenant}
        onClose={() => setIsCreateFromCsvDialogOpen(false)}
        onSuccess={(result) => {
          setIsCreateFromCsvDialogOpen(false);
          refreshDataLists();
          showSnackbar(t("dataLists.csv.createSuccess", { name: result.dataList.name, count: result.importedRows }));
        }}
      />

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </ListLayout>
  );
}
